import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, eachDayOfInterval, subDays, addDays } from 'date-fns';
import Icon from '../../../components/AppIcon';

const PredictiveTrendChart = ({ clients, workouts, dateRange }) => {
  const today = new Date();
  const daysToShow = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 90;
  const startDate = subDays(today, daysToShow);
  const endDate = addDays(today, 30);
  const dateInterval = eachDayOfInterval({ start: startDate, end: endDate });

  const historicalData = dateInterval?.slice(0, daysToShow)?.map(date => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayWorkouts = workouts?.filter(w => {
      const scheduledDate = format(new Date(w?.scheduledDate), 'yyyy-MM-dd');
      return scheduledDate === dateStr;
    });

    const scheduled = dayWorkouts?.length;
    const completed = dayWorkouts?.filter(w => w?.status === 'completed')?.length;
    const adherence = scheduled > 0 ? Math.round((completed / scheduled) * 100) : 0;

    return {
      date: format(date, 'MMM d'),
      adherence,
      completed,
      scheduled,
      isPrediction: false
    };
  });

  const recentAdherence = historicalData?.slice(-7)?.reduce((sum, d) => sum + d?.adherence, 0) / 7;
  const trend = recentAdherence > 70 ? 1.02 : recentAdherence > 50 ? 1.0 : 0.98;

  const predictedData = dateInterval?.slice(daysToShow)?.map((date, idx) => {
    const predictedAdherence = Math.min(100, Math.max(0, recentAdherence * Math.pow(trend, idx + 1)));
    
    return {
      date: format(date, 'MMM d'),
      adherence: null,
      predictedAdherence: Math.round(predictedAdherence),
      isPrediction: true
    };
  });

  const chartData = [...historicalData, ...predictedData];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const isPrediction = payload?.[0]?.payload?.isPrediction;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">
            {label} {isPrediction && '(Predicted)'}
          </p>
          <div className="space-y-1">
            {payload?.map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-muted-foreground">{entry?.name}:</span>
                <span className="font-semibold text-foreground">{entry?.value}%</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-blue-500" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Predictive Trend Forecast</h2>
            <p className="text-sm text-muted-foreground">Historical data with 30-day AI projection</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span className="text-sm text-muted-foreground">Historical</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-sm text-muted-foreground">Predicted</span>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="date" 
            className="text-xs text-muted-foreground"
            tick={{ fill: 'currentColor' }}
          />
          <YAxis 
            className="text-xs text-muted-foreground"
            tick={{ fill: 'currentColor' }}
            domain={[0, 100]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="adherence"
            stroke="hsl(var(--primary))"
            strokeWidth={3}
            dot={{ r: 4 }}
            name="Actual Adherence"
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="predictedAdherence"
            stroke="hsl(270, 70%, 60%)"
            strokeWidth={3}
            strokeDasharray="5 5"
            dot={{ r: 4 }}
            name="Predicted Adherence"
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Current 7-Day Average</p>
          <p className="text-2xl font-bold text-foreground">{Math.round(recentAdherence)}%</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Predicted 30-Day Trend</p>
          <p className="text-2xl font-bold text-foreground">
            {trend > 1.01 ? '📈 Improving' : trend < 0.99 ? '📉 Declining' : '➡️ Stable'}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Forecast Confidence</p>
          <p className="text-2xl font-bold text-foreground">
            {recentAdherence > 70 ? 'High' : recentAdherence > 50 ? 'Medium' : 'Low'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PredictiveTrendChart;